
import { useState } from 'react';
import { Modal } from '../../../components/base/Modal';
import { Button } from '../../../components/base/Button';
import ImageUpload from '../../../components/base/ImageUpload';
import TagInput from '../../../components/base/TagInput';
import { groupsApi, activitiesApi } from '../../../lib/api';
import { useAuth } from '../../../hooks/useAuth';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateGroupModal = ({ isOpen, onClose, onSuccess }: CreateGroupModalProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Entertainment',
    privacy: 'public',
    cover_image: '',
  });
  const [topics, setTopics] = useState<string[]>([]);
  const [rules, setRules] = useState<string[]>([]);
  const [newRule, setNewRule] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categories = [
    'Entertainment',
    'Production',
    'Genre',
    'Regional',
    'Professional',
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Group name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Group name must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if (!formData.cover_image) {
      newErrors.cover_image = 'Cover image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!user) {
      alert('You must be logged in to create a group');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the group via API
      const groupData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        cover_image: formData.cover_image,
        category: formData.category,
        privacy: formData.privacy,
        owner_id: user.id,
        member_count: 1,
        rules: rules.length > 0 ? rules : null,
        topics: topics.length > 0 ? topics : null,
        is_featured: false,
        is_trending: false,
      };

      const response = await groupsApi.createGroup(groupData);

      if (!response.success) {
        throw new Error(response.error || 'Failed to create group');
      }

      // Create activity via API (non-blocking)
      activitiesApi.createActivity({
        activityType: 'group_created',
        title: 'Created a new group',
        description: formData.name,
        data: {
          group_id: response.data?.id
        }
      }).catch(err => console.warn('Activity creation failed:', err));

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error creating group:', error);
      alert(error.message || 'Failed to create group. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Entertainment',
      privacy: 'public',
      cover_image: '',
    });
    setTopics([]);
    setRules([]);
    setNewRule('');
    setErrors({});
  };

  const handleAddRule = () => {
    if (newRule.trim() && rules.length < 20) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  const handleRemoveRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleImageUpload = (url: string) => {
    setFormData({ ...formData, cover_image: url });
    setErrors({ ...errors, cover_image: '' });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Group">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Group Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Group Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              setErrors({ ...errors, name: '' });
            }}
            placeholder="Enter group name..."
            maxLength={100}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.name ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {formData.name.length}/100 characters
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => {
              setFormData({ ...formData, description: e.target.value });
              setErrors({ ...errors, description: '' });
            }}
            placeholder="Describe what your group is about..."
            rows={4}
            maxLength={500}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none ${
              errors.description ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-500">{errors.description}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {formData.description.length}/500 characters
          </p>
        </div>

        {/* Cover Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cover Image <span className="text-red-500">*</span>
          </label>
          <ImageUpload
            onImageUpload={handleImageUpload}
            currentImage={formData.cover_image}
            aspectRatio="16:9"
            maxSize={4 * 1024 * 1024}
          />
          {errors.cover_image && (
            <p className="mt-1 text-sm text-red-500">{errors.cover_image}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Recommended size: 1200x675px (16:9 ratio). Max file size: 4MB
          </p>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Choose the category that best describes your group
          </p>
        </div>

        {/* Privacy */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Privacy <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="privacy"
                value="public"
                checked={formData.privacy === 'public'}
                onChange={(e) => setFormData({ ...formData, privacy: e.target.value })}
                className="mt-1 w-4 h-4 text-teal-500 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-global-line text-teal-600"></i>
                  <span className="font-medium text-gray-900">Public</span>
                </div>
                <p className="text-sm text-gray-600">
                  Anyone can see and join this group
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="privacy"
                value="private"
                checked={formData.privacy === 'private'}
                onChange={(e) => setFormData({ ...formData, privacy: e.target.value })}
                className="mt-1 w-4 h-4 text-teal-500 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-lock-line text-gray-600"></i>
                  <span className="font-medium text-gray-900">Private</span>
                </div>
                <p className="text-sm text-gray-600">
                  Only members can see posts. Join requests require approval
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Topics/Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topics/Tags
          </label>
          <TagInput
            tags={topics}
            onChange={setTopics}
            placeholder="Add topics (e.g., drama, comedy, action)..."
            maxTags={10}
          />
          <p className="mt-1 text-xs text-gray-500">
            Add up to 10 topics to help people find your group
          </p>
        </div>

        {/* Group Rules */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Group Rules
          </label>
          <div className="space-y-2 mb-3">
            {rules.map((rule, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </span>
                <span className="flex-1 text-sm text-gray-700">{rule}</span>
                <button
                  onClick={() => handleRemoveRule(index)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                  type="button"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddRule()}
              placeholder="Add a rule..."
              maxLength={200}
              disabled={rules.length >= 20}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <Button
              onClick={handleAddRule}
              variant="outline"
              disabled={!newRule.trim() || rules.length >= 20}
              className="whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>
              Add
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {rules.length}/20 rules added
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
        <Button onClick={handleClose} variant="outline" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          disabled={isSubmitting}
          className="whitespace-nowrap"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Creating...
            </>
          ) : (
            <>
              <i className="ri-add-line mr-2"></i>
              Create Group
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default CreateGroupModal;
